/**
 * Address book of the checkout and of the account page.
 *
 * Markup contract: one scope around the radio list and the address forms. `labels` carries the
 * texts of the delete dialog and is only needed where a remove button exists.
 *   <div x-data="addressPicker({ labels: { deleteTitle, deleteQuestion, deleteNo, deleteYes } })">
 *     <input type="radio" name="oxaddressid" value="<id>" @change="select('<id>')">
 *     <div class="deliveryaddress_form" data-oxaddress-id="<id>">…</div>
 *     <a data-element-id="delivery-address-modify-button" @click="edit('<id>')">
 *     <a data-element-id="delivery-address-remove-button" @click.prevent="confirmDelete($el.href)">
 *     <a data-element-id="billing-address-modify-button" @click="editBilling()">
 *     <div class="billingaddress_form">…</div>
 *   </div>
 *
 * Only the chosen address may reach the server, so every other address form is a disabled
 * fieldset. That also takes its fields out of the validation without touching their own
 * disabled flag, which countryState needs for the state select. blSaveAddress and blSaveInvoiceAddress
 * switch on with the pencil, which is what tells the server to store the edited address.
 */
import { clearRegistrationState } from './registrationState.js';

// the forms that open with their radio: a new address, a new packstation, the store
const ALWAYS_OPEN = ['-1', '-2', 'filialdelivery'];

document.addEventListener('alpine:init', () => {
    Alpine.data('addressPicker', ({ editing = '', billingEditing = false, labels } = {}) => ({
        selected: '',
        editing,
        billingEditing,
        labels,

        init() {
            this.selected = this.$el.querySelector('input[name="oxaddressid"]:checked')?.value ?? '';

            // reaching this form means the registration is over and its saved state is stale
            if (this.$el.closest('#changeUserAddressForm')) {
                clearRegistrationState();
            }

            this.$el.addEventListener('reveal-field', (event) => this.reveal(event.target));

            Alpine.effect(() => this.apply());
        },

        /** A hidden field failed the validation: open the address form it belongs to. */
        reveal(field) {
            const form = field.closest('.deliveryaddress_form[data-oxaddress-id]');

            if (form) {
                this.edit(form.dataset.oxaddressId);
            } else if (field.closest('.billingaddress_form')) {
                this.editBilling();
            }
            // the validation focuses the field as soon as this event returns, not after the effect
            this.apply();
        },

        forms() {
            return [...this.$el.querySelectorAll('.deliveryaddress_form[data-oxaddress-id]')];
        },

        isOpen(id) {
            return id === this.selected && (id === this.editing || ALWAYS_OPEN.includes(id));
        },

        select(id) {
            this.selected = id;
            this.editing = ALWAYS_OPEN.includes(id) ? id : '';
        },

        edit(id) {
            this.selected = id;
            this.editing = id;
        },

        editBilling() {
            this.billingEditing = true;
        },

        apply() {
            this.forms().forEach((form) => {
                const id = form.dataset.oxaddressId;
                const chosen = id === this.selected;

                form.classList.toggle('hidden', !this.isOpen(id));
                form.disabled = !chosen;

                const save = form.querySelector('[name="blSaveAddress"]');
                if (save) {
                    save.disabled = !(chosen && this.editing === id);
                }
            });

            // the billing address is always sent, the pencil only decides whether it is stored
            const billingForm = this.$el.querySelector('.billingaddress_form');
            if (billingForm) {
                billingForm.classList.toggle('hidden', !this.billingEditing);
                const save = billingForm.querySelector('[name="blSaveInvoiceAddress"]');
                if (save) {
                    save.disabled = !this.billingEditing;
                }
            }

            this.showSelectionHint();
        },

        /** The hint and the second submit sit in the sticky basket, which the checkout module renders. */
        showSelectionHint() {
            document.getElementById('cr_stickyMainFormSubmit')?.classList.toggle('disabled', !this.selected);
            document.getElementById('cr_stickyMainFormSubmit')?.toggleAttribute('disabled', !this.selected);

            const hint = document.getElementById('crev_selection_invalid');
            if (!hint) {
                return;
            }

            // only a saved address counts; the entries for a new address are always there
            const hasAddresses = this.$el.querySelector('#widgetShippingAddress_1') !== null;
            hint.style.display = this.selected ? 'none' : '';
            hint.querySelector('.crev_selectaddress')?.style.setProperty('display', hasAddresses ? '' : 'none');
            hint.querySelector('.crev_nodeladdress')?.style.setProperty('display', hasAddresses ? 'none' : '');
        },

        confirmDelete(url) {
            Alpine.store('dialog').openHtml(
                `<p>${this.labels.deleteQuestion}</p>`
                + '<div class="page-dialog--actions">'
                + `<button type="button" class="btn-secondary" @click="$store.dialog.close()">${this.labels.deleteNo}</button>`
                + `<a class="btn-primary" href="${url}">${this.labels.deleteYes}</a>`
                + '</div>',
                { title: this.labels.deleteTitle, className: 'page-dialog--narrow', dismissible: false },
            );
        },
    }));
});
